import {
  FormField,
  TableSection,
} from '@shared/components/add-form/add-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { InboundType, InboundWarrantStatus } from '../inbound.config';

// Inbound Add Form Data Interface
export interface InboundAddFormData {
  tenantId: number;
  inputType: string;
  returnsName: string;
  returnsAddress: string;
  returnsTel: string;
  country: string;
  province: string;
  city: string;
  district: string;
  sorBuyerNikeName: string;
  orderMsg: string;
  buyMsg: string;
  receivingLogicWarehouseId: number;
  receivingLogicWarehouseName: string;
  receivingLogicWarehouseCode: string;
  storeId: number;
  storeName: string;
  channelTypeCode: string;
  frontRecordCode: string;
  frontRecordId: number;
  platformOrderCode: string;
  orderCode: string;
  status: string;
}

// Logistics Item Interface
export interface LogisticsItem {
  id: string;
  logisticsCompanyId: number;
  logisticsCompanyName: string;
  logisticsNo: string;
}

// Detail Item Interface
export interface DetailItem {
  id: string;
  skuCode: string;
  skuName: string;
  spuCode: string;
  spuName: string;
  plannedQty: number;
  batchNumber?: string;
  expiryDate?: string;
}

// Inbound Type Options
export const INBOUND_TYPE_OPTIONS = [
  {
    label: 'inbound.types.purchase_order',
    value: InboundType.PURCHASE_ORDER,
    description: 'inbound.types.purchase_order_description',
  },
  {
    label: 'inbound.types.transfer_order',
    value: InboundType.TRANSFER_ORDER,
    description: 'inbound.types.transfer_order_description',
    disabled: true, // Temporarily disabled
  },
  {
    label: 'inbound.types.return_order',
    value: InboundType.RETURN_ORDER,
    description: 'inbound.types.return_order_description',
    disabled: true, // Temporarily disabled
  },
];

// Inbound Status Options
export const INBOUND_WARRANT_STATUS_OPTIONS = [
  { label: 'inbound.status.created', value: InboundWarrantStatus.CREATED },
  { label: 'inbound.status.in_transit', value: InboundWarrantStatus.IN_TRANSIT },
  { label: 'inbound.status.received', value: InboundWarrantStatus.RECEIVED },
  { label: 'inbound.status.completed', value: InboundWarrantStatus.COMPLETED },
  { label: 'inbound.status.cancelled', value: InboundWarrantStatus.CANCELLED },
];

// Form Fields Configuration
export const INBOUND_ADD_FORM_CONFIG: FormField[] = [
  // Basic Information (First Section)
  {
    label: 'inbound.fields.inbound_type',
    controlName: 'inputType',
    type: 'dropdown',
    placeholder: 'inbound.placeholders.select_inbound_type',
    section: 'inbound.sections.basic_information',
    required: true,
    options: INBOUND_TYPE_OPTIONS,
    defaultValue: InboundType.PURCHASE_ORDER,
  },
  {
    label: 'inbound.fields.inbound_status',
    controlName: 'status',
    type: 'dropdown',
    placeholder: 'inbound.placeholders.select_status',
    section: 'inbound.sections.basic_information',
    required: false,
    disabled: true,
    options: INBOUND_WARRANT_STATUS_OPTIONS,
    defaultValue: InboundWarrantStatus.CREATED,
  },

  // Additional Basic Information Fields (keep in same section)
  {
    label: 'inbound.fields.preceding_document_code',
    controlName: 'frontRecordCode',
    type: 'searchableDropdown',
    placeholder: 'inbound.placeholders.select_preceding_document_code',
    section: 'inbound.sections.basic_information',
    required: true,
    options: [], // Will be populated dynamically with PO list
    showClear: true,
  },
  {
    label: 'inbound.fields.inbound_notice',
    controlName: 'inboundNoticeId',
    type: 'searchableDropdown',
    placeholder: 'inbound.placeholders.select_inbound_notice',
    section: 'inbound.sections.basic_information',
    required: true,
    options: [], // Will be populated dynamically when PO is selected
    showClear: true,
  },
  {
    label: 'inbound.fields.third_party_order_number',
    controlName: 'platformOrderCode',
    type: 'text',
    placeholder: 'inbound.placeholders.enter_third_party_order_number',
    section: 'inbound.sections.basic_information',
  },
  {
    label: 'inbound.fields.internal_order_number',
    controlName: 'orderCode',
    type: 'text',
    placeholder: 'inbound.placeholders.enter_internal_order_number',
    section: 'inbound.sections.basic_information',
  },

  // Returner Information
  {
    label: 'inbound.fields.returner_name',
    controlName: 'returnsName',
    type: 'text',
    placeholder: 'inbound.placeholders.enter_returner_name',
    section: 'inbound.sections.returner_information',
  },
  {
    label: 'inbound.fields.returner_address',
    controlName: 'returnsAddress',
    type: 'text',
    placeholder: 'inbound.placeholders.enter_returner_address',
    section: 'inbound.sections.returner_information',
  },
  {
    label: 'inbound.fields.returner_phone',
    controlName: 'returnsTel',
    type: 'text',
    placeholder: 'inbound.placeholders.enter_returner_phone',
    maxLength: 20,
    section: 'inbound.sections.returner_information',
  },

  // Customer Information
  {
    label: 'inbound.fields.buyer_nickname',
    controlName: 'sorBuyerNikeName',
    type: 'text',
    placeholder: 'inbound.placeholders.enter_buyer_nickname',
    section: 'inbound.sections.customer_information',
  },
  {
    label: 'inbound.fields.order_message',
    controlName: 'orderMsg',
    type: 'text',
    placeholder: 'inbound.placeholders.enter_order_message',
    section: 'inbound.sections.customer_information',
  },
  {
    label: 'inbound.fields.customer_message',
    controlName: 'buyMsg',
    type: 'text',
    placeholder: 'inbound.placeholders.enter_customer_message',
    section: 'inbound.sections.customer_information',
  },

  // Warehouse Information
  {
    label: 'inbound.fields.receiving_logic_warehouse_id',
    controlName: 'receivingLogicWarehouseId',
    type: 'number',
    placeholder: 'inbound.placeholders.enter_receiving_logic_warehouse_id',
    section: 'inbound.sections.warehouse_information',
    useGrouping: false,
    hidden: true, // Hidden but kept for form submission
  },
  {
    label: 'inbound.fields.warehouse_code',
    controlName: 'receivingLogicWarehouseCode',
    type: 'text',
    placeholder: 'inbound.placeholders.auto_filled_after_warehouse_selection',
    section: 'inbound.sections.warehouse_information',
    readonly: true,
    required: false,
    colSpan: 1,
  },
  {
    label: 'inbound.fields.warehouse_name',
    controlName: 'receivingLogicWarehouseName',
    type: 'text',
    placeholder: 'inbound.placeholders.auto_filled_after_warehouse_selection',
    section: 'inbound.sections.warehouse_information',
    readonly: true,
    required: false,
    colSpan: 1,
  },

  // Store Information
  {
    label: 'inbound.fields.store_id',
    controlName: 'storeId',
    type: 'number',
    placeholder: 'inbound.placeholders.enter_store_id',
    section: 'inbound.sections.store_information',
    useGrouping: false,
    hidden: true, // Hidden but kept for form submission
  },
  {
    label: 'inbound.fields.store_name',
    controlName: 'storeName',
    type: 'text',
    placeholder: 'inbound.placeholders.enter_store_name',
    section: 'inbound.sections.store_information',
  },
  {
    label: 'inbound.fields.channel_type_code',
    controlName: 'channelTypeCode',
    type: 'text',
    placeholder: 'inbound.placeholders.enter_channel_type_code',
    section: 'inbound.sections.store_information',
  },
];

// Logistics Table Columns
export const LOGISTICS_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'inbound.table.columns.logistics_company_id',
    prop: 'logisticsCompanyId',
    width: 180,
  },
  {
    label: 'inbound.table.columns.logistics_company_name',
    prop: 'logisticsCompanyName',
    width: 200,
  },
  {
    label: 'inbound.table.columns.logistics_number',
    prop: 'logisticsNo',
    width: 180,
  },
  {
    label: 'inbound.table.columns.actions',
    prop: 'actions',
    type: 'actions',
    width: 120,
  },
];

// Detail Table Columns
export const DETAIL_TABLE_COLUMNS: TableColumn[] = [
  { label: 'inbound.table.columns.sku_code', prop: 'skuCode', width: 150 },
  { label: 'inbound.table.columns.sku_name', prop: 'skuName', width: 200 },
  { label: 'inbound.table.columns.spu_code', prop: 'spuCode', width: 150 },
  { label: 'inbound.table.columns.spu_name', prop: 'spuName', width: 200 },
  {
    label: 'inbound.table.columns.planned_quantity',
    prop: 'plannedQty',
    width: 150,
    type: 'text', // View only - changed from inputNumberHorizontal
  },
  { label: 'inbound.fields.lot', prop: 'batchNumber', width: 150 },
  { label: 'inbound.fields.expiry', prop: 'expiryDate', width: 150 },
];

// Logistics Table Section
export const INBOUND_LOGISTICS_TABLE_SECTION: TableSection = {
  title: 'inbound.sections.logistics_information',
  name: 'logistics_information', // Stable identifier for comparison
  show: true,
  columns: LOGISTICS_TABLE_COLUMNS,
  data: [],
  actions: [
    {
      label: 'inbound.actions.delete',
      action: 'deleteSelectedLogistics',
      severity: 'danger',
      outlined: true,
      disabled: true,
      icon: 'delete',
    },
    {
      label: 'common.actions.new',
      action: 'addLogistics',
      severity: 'info',
      icon: 'add',
    },
  ],
  hasCheckbox: true,
};

// Detail Table Section
export const INBOUND_DETAIL_TABLE_SECTION: TableSection = {
  title: 'inbound.sections.item_details',
  name: 'item_details', // Stable identifier for comparison
  show: true,
  columns: DETAIL_TABLE_COLUMNS,
  data: [],
  actions: [], // View only - no actions
  hasCheckbox: false, // No checkbox for view only
};

// Header Actions
export const INBOUND_HEADER_ACTIONS = [
  { label: 'common.actions.save', action: 'save', severity: 'info' as const },
  {
    label: 'common.actions.cancel',
    action: 'cancel',
    outlined: true,
    severity: 'secondary' as const,
  },
];
